import { useEffect, useState, useCallback, useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus, Trash2 } from 'lucide-react';
import {
  getTracks,
  createTrack,
  updateTrack,
  deleteTrack,
  getTrackUsers,
  getUsers,
  assignTrackToUser,
  removeTrackFromUser,
} from '../../services/api';
import { getErrorMessage } from '../../utils/errors';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import AssignmentList from '../../components/admin/AssignmentList';
import type { AssignedUser, Track, UserWithRoles } from '../../types';
import { PageHeader, Alert, Button, DataTable, Field, Input, Separator, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@racing/ui';

const TIMING_SYSTEMS = ['COMPULINK', 'PORTATREE', 'OTHER'];

const TracksPage = () => {
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTrack, setEditingTrack] = useState<Partial<Track>>({});
  const [isEdit, setIsEdit] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const [assignedUsers, setAssignedUsers] = useState<AssignedUser[]>([]);
  const [allUsers, setAllUsers] = useState<UserWithRoles[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const loadTracks = useCallback(async () => {
    try {
      setLoading(true);
      setTracks(await getTracks());
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTracks();
  }, [loadTracks]);

  const loadAssignments = useCallback(async (trackId: number) => {
    try {
      setUsersLoading(true);
      const [assigned, all] = await Promise.all([getTrackUsers(trackId), getUsers()]);
      setAssignedUsers(assigned);
      setAllUsers(all);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const openEdit = (track: Track) => {
    setEditingTrack({ ...track });
    setIsEdit(true);
    setDialogOpen(true);
    setAssignedUsers([]);
    loadAssignments(track.track_id);
  };

  const handleSave = async () => {
    try {
      if (isEdit && editingTrack.track_id) {
        await updateTrack(editingTrack.track_id, editingTrack);
      } else {
        await createTrack(editingTrack);
      }
      setDialogOpen(false);
      loadTracks();
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  };

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    if (pendingDeleteId === null) return;
    try {
      await deleteTrack(pendingDeleteId);
      loadTracks();
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
    setPendingDeleteId(null);
  };

  /**
   * Only track operators can hold a track, and only ones not already on it.
   * The server enforces this too (hasTrackAccess); filtering here just avoids
   * offering a choice that would be rejected.
   */
  const availableUsers = useMemo(() => {
    const assignedIds = new Set(assignedUsers.map((u) => u.user_id));
    return allUsers.filter(
      (user) =>
        user.roles?.some((role) => role.role_name === 'TRACK_OPERATOR') &&
        !assignedIds.has(user.id)
    );
  }, [allUsers, assignedUsers]);

  const columns = useMemo<ColumnDef<Track, any>[]>(
    () => [
      { accessorKey: 'track_name', header: 'Track Name', meta: { width: 220 } },
      { accessorKey: 'city', header: 'City', meta: { width: 150 } },
      { accessorKey: 'state', header: 'State', meta: { width: 100, priority: 'sm' } },
      { accessorKey: 'zip_code', header: 'Zip', meta: { width: 110, priority: 'lg' } },
      { accessorKey: 'timing_system', header: 'Timing System', meta: { width: 160, priority: 'md' } },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Delete ${row.original.track_name}`}
            className="text-danger hover:bg-danger-bg"
            onClick={(event) => {
              event.stopPropagation();
              setPendingDeleteId(row.original.track_id);
              setConfirmOpen(true);
            }}
          >
            <Trash2 />
          </Button>
        ),
        meta: { width: 60, incidental: true },
      },
    ],
    []
  );

  return (
    <div className="flex min-w-0 flex-col">
      <PageHeader
        title="Tracks"
        subtitle="Venues that run events. Assigning a track operator is what grants them access to its events."
        actions={
          <Button
            className="w-full sm:w-auto"
            onClick={() => {
              setEditingTrack({});
              setIsEdit(false);
              setDialogOpen(true);
            }}
          >
            <Plus /> Add Track
          </Button>
        }
      />

      {error && (
        <Alert variant="danger" className="mb-4" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <DataTable
        data={tracks}
        columns={columns}
        getRowId={(row) => String(row.track_id)}
        loading={loading}
        onRowClick={openEdit}
        emptyMessage="No tracks yet"
        pageSizeOptions={[10, 25, 50]}
        className="h-[calc(100dvh-16rem)] min-h-[22rem]"
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit track' : 'Add track'}</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Track Name" required className="sm:col-span-2">
                {(p) => (
                  <Input
                    value={editingTrack.track_name || ''}
                    onChange={(e) =>
                      setEditingTrack({ ...editingTrack, track_name: e.target.value })
                    }
                    {...p}
                  />
                )}
              </Field>
              <Field label="City">
                {(p) => (
                  <Input
                    value={editingTrack.city || ''}
                    onChange={(e) => setEditingTrack({ ...editingTrack, city: e.target.value })}
                    {...p}
                  />
                )}
              </Field>
              <Field label="State">
                {(p) => (
                  <Input
                    value={editingTrack.state || ''}
                    onChange={(e) => setEditingTrack({ ...editingTrack, state: e.target.value })}
                    {...p}
                  />
                )}
              </Field>
              <Field label="Zip">
                {(p) => (
                  <Input
                    value={editingTrack.zip_code || ''}
                    onChange={(e) => setEditingTrack({ ...editingTrack, zip_code: e.target.value })}
                    {...p}
                  />
                )}
              </Field>
              <Field label="Timing System">
                {(p) => (
                  <Select
                    value={editingTrack.timing_system || 'COMPULINK'}
                    onValueChange={(value) =>
                      setEditingTrack({ ...editingTrack, timing_system: value })
                    }
                  >
                    <SelectTrigger id={p.id}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMING_SYSTEMS.map((system) => (
                        <SelectItem key={system} value={system}>
                          {system}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>

            {isEdit && editingTrack.track_id && (
              <>
                <Separator />
                <section className="flex flex-col gap-3">
                  <h3 className="font-sans text-xs font-semibold uppercase tracking-[0.12em] text-ink-faint">
                    Assigned users
                  </h3>
                  <AssignmentList
                    items={assignedUsers.map((user) => ({
                      id: user.user_id,
                      primary: user.display_name || user.username,
                      secondary: user.display_name ? user.username : null,
                    }))}
                    options={availableUsers.map((user) => ({
                      id: user.id,
                      label: user.display_name || user.username,
                    }))}
                    addLabel="Add user"
                    emptyMessage="No users assigned. Nobody can manage events here until someone is."
                    loading={usersLoading}
                    onAdd={async (userId: number) => {
                      try {
                        await assignTrackToUser(userId, editingTrack.track_id!);
                        loadAssignments(editingTrack.track_id!);
                      } catch (err: unknown) {
                        setError(getErrorMessage(err));
                      }
                    }}
                    onRemove={async (userId: number) => {
                      try {
                        await removeTrackFromUser(userId, editingTrack.track_id!);
                        loadAssignments(editingTrack.track_id!);
                      } catch (err: unknown) {
                        setError(getErrorMessage(err));
                      }
                    }}
                  />
                </section>
              </>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete track?"
        message="Events already recorded against this track are not deleted, but the track will no longer be selectable."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setConfirmOpen(false);
          setPendingDeleteId(null);
        }}
      />
    </div>
  );
};

export default TracksPage;
